import React, { Children } from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';

import { CreateFolderEventAdapter } from '~analytics/adapters/CreateFolderEventAdapter';
import { IconButton, SmallButton } from '~shared/components/buttons';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import {
  IconChevron,
  IconDocument,
  IconFolder,
  IconFolderAdd,
  IconUser,
} from '~shared/components/icons/standard';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { useQuizzes } from '~teacher/hook';
import { useCreateFolder } from '~teacher/hook/useCreateFolder';

import { FolderType } from '../../../../api/folders';
import { uniqueIdFactory } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';
import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';

interface ListProps {
  children: React.ReactNode;
}
const List = ({ ...props }: ListProps): React.ReactElement => {
  return Children.count(props.children) === 0 ? (
    <div className="p-16 flex justify-center content-center text-md font-display">
      {translate('This folder is empty')}
    </div>
  ) : (
    <ol className="w-max"> {props.children}</ol>
  );
};

interface ListItemProps {
  onClick: (e?: React.MouseEvent<HTMLElement>) => void;
  item: {
    name: string;
    type: string;
    uniqueId: string;
    disable?: boolean;
  };
}
const ListItem = ({ onClick, item }: ListItemProps): React.ReactElement => {
  const listClass = cx(
    'flex flex-row items-center justify-start py-4',
    item.type === 'folder' && !item.disable
      ? 'cursor-pointer'
      : 'cursor-default'
  );
  const quizIconClass = cx(
    'flex items-center justify-start min-w-6 text-grey-800'
  );
  const folderIconClass = cx(
    'flex items-center justify-start min-w-30',
    item.disable && 'opacity-50'
  );
  const textClass = cx('pl-6 group text-base font-500 leading-8 w-full', {
    'text-blue-900 hover:text-cyan-700 truncate':
      item.type === 'folder' && !item.disable,
    'text-grey-800 hover:text-grey-800': item.disable,
    'text-grey-800': item.type === 'quiz',
  });

  return (
    <>
      <li className={listClass} onClick={item.disable ? () => {} : onClick}>
        {item.type === 'quiz' && (
          <div className={quizIconClass}>
            <IconDocument />
          </div>
        )}
        {item.type === 'folder' && (
          <div className={folderIconClass}>
            <IconFolder />
          </div>
        )}
        <span className={textClass}>{item.name}</span>
        {item.type === 'folder' && (
          <div className={folderIconClass}>
            <IconChevron direction="right" />
          </div>
        )}
      </li>
      <Divider />
    </>
  );
};

interface QuizTraverseListProps {
  selectedParentFolder: number;
  onSelected: (item: string) => void;
  selectedItems?: QuizOrFolder[];
}

export const QuizTraverseList = ({
  selectedParentFolder,
  onSelected,
  selectedItems,
}: QuizTraverseListProps): React.ReactElement => {
  const { data } = useQuizzes();
  // object of all the items that belong to the user accessible via the id
  const allItems = data?.itemById || {};
  const quizzes = data?.itemIdByType.quiz || [];
  const folders = data?.itemIdByType.folder || [];
  const selectedFolders = new Set();

  if (selectedItems) {
    selectedItems.forEach((item) => {
      if (item.type === 'folder') {
        selectedFolders.add(item.uniqueId);
      }
    });
  }

  // function to get details of a quiz by its id
  const getQuizDetails = (id: string) => {
    return allItems[id];
  };

  const [openNewFolder, setOpenNewFolder] = React.useState<boolean>(false);

  // function that generates list of folders & quizzes within a folder directory
  const dateSort = (a: QuizOrFolder, b: QuizOrFolder) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  };
  const getListOfItems = () => {
    const updatedFolders = folders.filter(
      (folder) =>
        getQuizDetails(folder).parentId === selectedParentFolder &&
        !getQuizDetails(folder).deleted
    );
    const updatedQuizzes = quizzes.filter(
      (quiz) =>
        getQuizDetails(quiz).parentId === selectedParentFolder &&
        !getQuizDetails(quiz).deleted
    );
    return [
      ...updatedFolders.map(getQuizDetails).sort(dateSort),
      ...updatedQuizzes.map(getQuizDetails).sort(dateSort),
    ];
  };

  const goBack = () => {
    const currentItem = getQuizDetails(`folder-${selectedParentFolder}`);
    const itemParent =
      allItems[uniqueIdFactory.folder.toUniqueId(currentItem.parentId)];
    onSelected(itemParent?.uniqueId ?? null);
  };

  const handleClickMaster = (item: string) => {
    onSelected(item);
  };

  const selectedFolder =
    allItems[uniqueIdFactory.folder.toUniqueId(selectedParentFolder)];
  const parentFolder =
    allItems[uniqueIdFactory.folder.toUniqueId(selectedFolder?.parentId)];

  return (
    <>
      <Divider />
      {selectedParentFolder ? (
        <div className="flex items-center h-16">
          <IconButton
            onClick={goBack}
            aria-label={translate('Back to {0}').format(
              parentFolder?.name ?? translate('Personal')
            )}
          >
            <IconChevron direction="left" />
          </IconButton>
          <span className="text-base font-800 leading-8 text-blue-900 w-full truncate mr-3">
            {selectedFolder.name}
          </span>
          <IconButton onClick={() => setOpenNewFolder((state) => !state)}>
            <IconFolderAdd className="text-cyan-700" />
          </IconButton>
        </div>
      ) : (
        <div className="flex items-center h-16 w-full">
          <div className="flex flex-none">
            <IconUser />
          </div>
          <div className="pl-6 text-base font-800 leading-8 text-blue-900 w-full truncate mr-3">
            {translate('Personal')}
          </div>
          <IconButton onClick={() => setOpenNewFolder((state) => !state)}>
            <IconFolderAdd className="text-cyan-700" />
          </IconButton>
        </div>
      )}
      <Divider />
      <div className="h-80 overflow-y-scroll overflow-x-hidden">
        <List>
          {openNewFolder && (
            <NewFolder
              setOpenNewFolder={setOpenNewFolder}
              parent={selectedParentFolder}
            />
          )}
          {getListOfItems().map((folderOrQuiz) => {
            return (
              <ListItem
                key={folderOrQuiz.uniqueId}
                item={{
                  ...folderOrQuiz,
                  disable: selectedFolders.has(folderOrQuiz.uniqueId),
                }}
                onClick={() => {
                  setOpenNewFolder(false);
                  handleClickMaster(folderOrQuiz.uniqueId);
                }}
              />
            );
          })}
        </List>
      </div>
    </>
  );
};

function useCreateNewFolder({
  parentId,
  onClose,
  newFolderName,
}: {
  parentId: number;
  onClose: () => void;
  newFolderName: string;
}) {
  const { mutate, isLoading } = useCreateFolder();
  const saveFolder = (folderName: string) => {
    mutate(
      {
        name: folderName,
        parentId: parentId,
        type: FolderType.QUIZ_FOLDER,
      },
      {
        onSuccess: () => {
          new CreateFolderEventAdapter().sendAnalytics();
          onClose();
        },
        onError: (error) => {
          if (error instanceof Error) {
            toast(`Error: ${error.message}`, { tone: 'critical' });
          }
        },
      }
    );
  };

  const createNewFolder = () => saveFolder(newFolderName);

  return { createNewFolder, isLoading };
}

interface NewFolderProps {
  parent: number;
  setOpenNewFolder: (state: boolean) => void;
}
const NewFolder = ({ parent, setOpenNewFolder }: NewFolderProps) => {
  const [name, setName] = React.useState<string>('');
  const { createNewFolder } = useCreateNewFolder({
    parentId: parent ? parent : null,
    onClose: () => {
      setOpenNewFolder(false);
    },
    newFolderName: name,
  });
  const autoFocus = useAutoFocus();
  return (
    <>
      <div className="w-full flex flex-row py-4 sticky top-0 bg-white z-101">
        <div className="flex items-center justify-start min-w-30 mr-3">
          <IconFolder />
        </div>
        <TextInput
          ref={autoFocus}
          aria-label={translate('Folder Name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Untitled Folder"
          className="w-full mr-2"
        />
        <SmallButton
          onClick={() => {
            createNewFolder();
          }}
        >
          {translate('Save')}
        </SmallButton>
      </div>
      <Divider />
    </>
  );
};
